import React, { useEffect, useRef, useState } from "react";
import { Button, Modal, Form as BTForm, Spinner } from "react-bootstrap";
import axios from "src/config/axios";
import { Form, Formik } from "formik";
import FormInputText from "../../../../components/Forms/FormInputText";
import FormSelectBox from "../../../../components/Forms/FormSelectBox";
import FormCheckbox from "../../../../components/Forms/FormCheckbox";
import { showErrorToast } from "../../../../components/Toasts";

const EditAccessSystemModal = ({
  show,
  toggle,
  access_system = {},
  handleSuccess = () => {},
}) => {
  function submit(values, { setSubmitting, setErrors }) {
    setSubmitting(true);
    let request;

    if (access_system.id) {
      request = axios.put(`/api/v1/access_systems/${access_system.id}.json`, {
        access_system: values,
      });
    } else {
      request = axios.post(`/api/v1/access_systems.json`, {
        access_system: values,
      });
    }

    request
      .then(function ({ data }) {
        toggle();
        handleSuccess(data);
      })
      .catch(function (e) {
        setErrors(e.response.data);
      });
  }

  const formRef = useRef();
  const [payPerUseProducts, setPayPerUseProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRegenerateConfirmation, setShowRegenerateConfirmation] =
    useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regeneratedApiKey, setRegeneratedApiKey] = useState(null);

  const payPerUseOptions = payPerUseProducts.map((product) => ({
    name: product.display_name,
    value: product.id,
  }));
  const initialValues = {
    checkin_required: false,
    stay_period_required: false,
    ...access_system,
  };

  useEffect(() => {
    axios.get(`/api/v1/pay_per_use_products.json`).then(({ data }) => {
      setPayPerUseProducts(data.entries);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!show) {
      setShowRegenerateConfirmation(false);
      setRegeneratedApiKey(null);
    }
  }, [show]);

  const handleSubmit = () => {
    formRef.current.handleSubmit();
  };

  const handleRegenerateApiKey = () => {
    setRegenerating(true);

    axios
      .post(
        `/api/v1/access_systems/${access_system.id}/regenerate_api_key.json`
      )
      .then(({ data }) => {
        setRegeneratedApiKey(data.plain_api_key);
        setShowRegenerateConfirmation(false);
      })
      .catch(() => {
        showErrorToast("API-Key konnte nicht neu generiert werden");
      })
      .finally(() => {
        setRegenerating(false);
      });
  };

  function handleTypeChange(e, setFieldValue) {
    const nextTypeOf = e.target.value || null;
    setFieldValue("type_of", nextTypeOf);
    if (nextTypeOf === "gate_in" || nextTypeOf === "gate_out") {
      setFieldValue("stay_period_required", false);
    }
    if (nextTypeOf !== "door" && nextTypeOf !== "shower") {
      setFieldValue("checkin_required", false);
    }
  }

  function handleModalHide() {
    if (!regenerating) toggle();
  }

  return (
    <Modal show={show} onHide={handleModalHide}>
      <Modal.Header>
        <Modal.Title>
          {!access_system?.id ? "Erstellen" : "Bearbeiten"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <Spinner
            className={"d-block mx-auto"}
            animation="border"
            variant="primary"
          />
        ) : (
          <Formik
            innerRef={formRef}
            initialValues={initialValues}
            enableReinitialize={true}
            onSubmit={submit}
          >
            {({ values, setFieldValue }) => (
              <Form autoComplete="false">
                <FormInputText name="name" label="Name" />
                <FormSelectBox
                  name="type_of"
                  label={"Typ"}
                  onChange={(e) => handleTypeChange(e, setFieldValue)}
                  options={[
                    { name: "Tür", value: "door" },
                    { name: "Dusche", value: "shower" },
                    { name: "Waschmaschine", value: "wash_machine" },
                    { name: "Schranke Eingang", value: "gate_in" },
                    { name: "Schranke Ausgang", value: "gate_out" },
                  ]}
                />

                {!values.pay_per_use_product_id && (
                  <FormCheckbox
                    name="checkin_required"
                    label="Check-In benötigt"
                    hint={
                      "Nutzung erst nach abgeschlossenem Check-In erlaubt"
                    }
                  />
                )}

                {["door", "shower", "wash_machine"].includes(values.type_of) && (
                  <FormCheckbox
                    name="stay_period_required"
                    label="Nutzung nur innerhalb des Aufenthaltszeitraums"
                    hint="Nutzung nur zwischen der gebuchten Anreise- und Abreisezeit erlaubt"
                  />
                )}

                {(values.type_of === "shower" ||
                  values.type_of === "wash_machine") && (
                  <>
                    <FormSelectBox
                      name="pay_per_use_product_id"
                      label={"Pay Per Use Produkt"}
                      options={payPerUseOptions}
                    />

                    <FormInputText
                      name="lcd_text1"
                      label="LCD-Display"
                      hint={`Dieser Text wird auf dem LCD-Display angezeigt. Länge: ${
                        values.lcd_text1?.length ?? 0
                      }/40`}
                    />
                  </>
                )}
              </Form>
            )}
          </Formik>
        )}
        {access_system?.id && (
          <BTForm.Group className="mt-4 mb-0">
            {regeneratedApiKey ? (
              <>
                <BTForm.Label>Neuer API-Key</BTForm.Label>
                <BTForm.Control
                  readOnly
                  value={regeneratedApiKey}
                  onFocus={(event) => event.target.select()}
                />
                <BTForm.Text className="text-muted">
                  Kopiere den API-Key jetzt und aktualisiere das Gerät. Nach dem
                  Schließen wird er nicht mehr angezeigt.
                </BTForm.Text>
              </>
            ) : showRegenerateConfirmation ? (
              <>
                <BTForm.Text className="text-danger mb-2">
                  Der bisherige API-Key wird sofort ungültig. Das Gerät benötigt
                  danach den neuen Key.
                </BTForm.Text>
                <Button
                  type="button"
                  variant="danger"
                  disabled={regenerating}
                  onClick={handleRegenerateApiKey}
                >
                  {regenerating
                    ? "Generiere..."
                    : "API-Key jetzt neu generieren"}
                </Button>{" "}
                <Button
                  type="button"
                  variant="secondary"
                  disabled={regenerating}
                  onClick={() => setShowRegenerateConfirmation(false)}
                >
                  Abbrechen
                </Button>
              </>
            ) : (
              <Button
                type="button"
                variant="outline-danger"
                onClick={() => setShowRegenerateConfirmation(true)}
              >
                API-Key neu generieren
              </Button>
            )}
          </BTForm.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" disabled={regenerating} onClick={toggle}>
          Schließen
        </Button>
        <Button
          variant="primary"
          disabled={regenerating || showRegenerateConfirmation}
          onClick={handleSubmit}
        >
          Speichern
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditAccessSystemModal;
